import React, { useState, useContext, useMemo } from "react";
import { useAccountsNav } from "../../utils/moduleNav";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  TextField,
} from "@mui/material";
import {
  KeyboardArrowDown,
  FolderOpen,
  Folder,
  UnfoldMore,
  UnfoldLess,
  Close,
} from "@mui/icons-material";
import { format } from "date-fns";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import axios from "axios";

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(amount || 0);

const CATEGORY_META = {
  ASSET: { accent: "#2563EB", badge: "#1D4ED8", label: "Asset" },
  LIABILITY: { accent: "#DC2626", badge: "#B91C1C", label: "Liability" },
  INCOME: { accent: "#16A34A", badge: "#15803D", label: "Income" },
  EXPENSE: { accent: "#D97706", badge: "#B45309", label: "Expense" },
};

const collectAllIds = (nodes, result = []) => {
  nodes.forEach((node) => {
    if (node.account_type !== "ACCOUNT") {
      result.push(String(node.account_id));
      if (node.children?.length) collectAllIds(node.children, result);
    }
  });
  return result;
};

const flattenTree = (
  nodes,
  expandedIds,
  showZeroBalances,
  depth = 0,
  ancestors = [],
) => {
  const rows = [];
  nodes.forEach((node) => {
    const visible = ancestors.every((aid) => expandedIds.has(aid));
    if (
      !showZeroBalances &&
      node.account_type === "ACCOUNT" &&
      node.total_debit === 0 &&
      node.total_credit === 0
    )
      return;

    rows.push({ node, depth, visible });

    if (node.children?.length) {
      const childDepth = node.account_type === "CATEGORY" ? 0 : depth + 1;
      const childAncestors =
        node.account_type === "CATEGORY"
          ? [String(node.account_id)]
          : [...ancestors, String(node.account_id)];
      rows.push(
        ...flattenTree(
          node.children,
          expandedIds,
          showZeroBalances,
          childDepth,
          childAncestors,
        ),
      );
    }
  });
  return rows;
};

const EASE = "280ms cubic-bezier(0.4, 0, 0.2, 1)";

const CellInner = ({
  visible,
  align = "left",
  py = "6px",
  px = "16px",
  children,
}) => (
  <Box
    sx={{
      maxHeight: visible ? "80px" : "0px",
      opacity: visible ? 1 : 0,
      overflow: "hidden",
      transition: `max-height ${EASE}, opacity ${EASE}, padding ${EASE}`,
      px,
      py: visible ? py : "0px",
      display: "flex",
      alignItems: "center",
      justifyContent: align === "right" ? "flex-end" : "flex-start",
    }}
  >
    {children}
  </Box>
);

const MonoAmount = ({ value, color, size = "0.78rem", weight = 500 }) => (
  <Typography
    sx={{
      fontFamily: "'IBM Plex Mono', 'Fira Code', monospace",
      fontSize: size,
      fontWeight: weight,
      color,
      fontVariantNumeric: "tabular-nums",
      letterSpacing: "0.01em",
    }}
  >
    {formatCurrency(value)}
  </Typography>
);

const Dash = () => (
  <Typography
    sx={{
      color: "#CBD5E1",
      fontSize: "0.72rem",
      fontFamily: "'IBM Plex Mono', monospace",
    }}
  >
    —
  </Typography>
);

const BalanceChip = ({ type }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      ml: 0.5,
      px: "5px",
      py: "2px",
      borderRadius: "10px",
      fontSize: "0.6rem",
      fontWeight: 700,
      lineHeight: 1,
      color: "white",
      backgroundColor: "#2563EB",
    }}
  >
    {type}
  </Box>
);

// ─────────────────────────────────────────────────────────────
// TRIAL ROW
// ─────────────────────────────────────────────────────────────
const TrialRow = ({
  node,
  depth,
  visible,
  expandedIds,
  onToggle,
  onAccountDoubleClick,
  filterMode,
}) => {
  const isCategory = node.account_type === "CATEGORY";
  const isGroup = node.account_type === "GROUP";
  const isAccount = node.account_type === "ACCOUNT";
  const hasChildren = node.children?.length > 0;
  const isExpanded = expandedIds.has(String(node.account_id));
  const isDebit = node.balance_type === "DEBIT";
  const isOpeningDebit = node.opening_balance_type === "DEBIT";
  const meta = isCategory ? CATEGORY_META[node.account_category] : null;
  const nameIndent = isCategory ? "8px" : `${8 + depth * 20}px`;
  const rowPy = isCategory ? "9px" : isGroup ? "7px" : "5px";

  return (
    <Tooltip
      title={isAccount ? "Double-click to view ledger" : ""}
      placement="left"
      disableHoverListener={!isAccount}
    >
      <TableRow
        onDoubleClick={() => isAccount && onAccountDoubleClick(node)}
        sx={{
          background: isCategory
            ? `linear-gradient(90deg, ${meta.accent}10 0%, #FFFFFF 55%)`
            : isGroup && depth === 0
              ? "#F8FAFC"
              : "transparent",
          borderLeft: isCategory
            ? `3px solid ${meta.accent}`
            : "3px solid transparent",
          cursor: isAccount ? "pointer" : "default",
          "&:hover .cell-inner": {
            backgroundColor: isCategory ? `${meta.accent}07` : "#F8FAFC",
          },
          "& td": {
            p: "0 !important",
            borderBottom: visible
              ? `1px solid ${isCategory ? `${meta.accent}20` : "#F1F5F9"}`
              : "none",
            transition: `border-color ${EASE}`,
          },
        }}
      >
        {/* Toggle */}
        <TableCell sx={{ width: 36 }}>
          <CellInner visible={visible} align="center" py={rowPy} px="6px">
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={() => onToggle(String(node.account_id))}
                sx={{
                  p: "2px",
                  color: isCategory ? meta.accent : "#94A3B8",
                  transition: `transform 280ms cubic-bezier(0.4,0,0.2,1), color 150ms ease`,
                  transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                  "&:hover": {
                    background: "transparent",
                    color: isCategory ? meta.accent : "#475569",
                  },
                }}
              >
                <KeyboardArrowDown sx={{ fontSize: 16 }} />
              </IconButton>
            ) : (
              <Box sx={{ width: 24 }} />
            )}
          </CellInner>
        </TableCell>

        {/* Name */}
        <TableCell>
          <CellInner
            visible={visible}
            py={rowPy}
            px={nameIndent}
            className="cell-inner"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {isCategory && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: "7px",
                    py: "1.5px",
                    borderRadius: "3px",
                    backgroundColor: meta.badge,
                    color: "#fff",
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    flexShrink: 0,
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  {meta.label}
                </Box>
              )}
              {isGroup &&
                (isExpanded ? (
                  <FolderOpen
                    sx={{ fontSize: 14, color: "#F59E0B", flexShrink: 0 }}
                  />
                ) : (
                  <Folder
                    sx={{ fontSize: 14, color: "#F59E0B", flexShrink: 0 }}
                  />
                ))}
              {isAccount && (
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    backgroundColor: "#CBD5E1",
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography
                sx={{
                  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                  fontWeight: isCategory ? 700 : isGroup ? 600 : 400,
                  fontSize: isCategory
                    ? "0.82rem"
                    : isGroup
                      ? "0.79rem"
                      : "0.76rem",
                  color: isCategory
                    ? meta.accent
                    : isGroup
                      ? "#1E293B"
                      : "#475569",
                  letterSpacing: isCategory ? "0.02em" : "normal",
                }}
              >
                {node.account_name}
              </Typography>
              {node.account_code && (
                <Typography
                  sx={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: "0.63rem",
                    color: "#94A3B8",
                    flexShrink: 0,
                  }}
                >
                  {node.account_code}
                </Typography>
              )}
            </Box>
          </CellInner>
        </TableCell>

        {/* Opening Balance (Between mode only) */}
        {filterMode === "between" && (
          <TableCell sx={{ minWidth: 140 }}>
            <CellInner visible={visible} align="right" py={rowPy}>
              {node.opening_balance > 0 ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <MonoAmount
                    value={node.opening_balance}
                    color={isOpeningDebit ? "#DC2626" : "#000000"}
                    size={isCategory ? "0.82rem" : "0.78rem"}
                    weight={isCategory || isGroup ? 700 : 500}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: isOpeningDebit ? "#DC2626" : "#000000",
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}
                  >
                    {isOpeningDebit ? "Db" : "Cr"}
                  </Typography>
                </Box>
              ) : (
                <Dash />
              )}
            </CellInner>
          </TableCell>
        )}

        {/* Debit — red */}
        <TableCell sx={{ minWidth: 140 }}>
          <CellInner visible={visible} align="right" py={rowPy}>
            {filterMode === "between" ? (
              node.period_debit > 0 ? (
                <MonoAmount
                  value={node.period_debit}
                  color="#DC2626"
                  size={isCategory ? "0.82rem" : "0.78rem"}
                  weight={isCategory || isGroup ? 700 : 500}
                />
              ) : (
                <Dash />
              )
            ) : node.total_debit > 0 ? (
              <MonoAmount
                value={node.total_debit}
                color="#DC2626"
                size={isCategory ? "0.82rem" : "0.78rem"}
                weight={isCategory || isGroup ? 700 : 500}
              />
            ) : (
              <Dash />
            )}
          </CellInner>
        </TableCell>

        {/* Credit — black */}
        <TableCell sx={{ minWidth: 140 }}>
          <CellInner visible={visible} align="right" py={rowPy}>
            {filterMode === "between" ? (
              node.period_credit > 0 ? (
                <MonoAmount
                  value={node.period_credit}
                  color="#000000"
                  size={isCategory ? "0.82rem" : "0.78rem"}
                  weight={isCategory || isGroup ? 700 : 500}
                />
              ) : (
                <Dash />
              )
            ) : node.total_credit > 0 ? (
              <MonoAmount
                value={node.total_credit}
                color="#000000"
                size={isCategory ? "0.82rem" : "0.78rem"}
                weight={isCategory || isGroup ? 700 : 500}
              />
            ) : (
              <Dash />
            )}
          </CellInner>
        </TableCell>

        {/* Closing Balance (Between mode only) OR Balance (As On mode) */}
        <TableCell sx={{ minWidth: 140 }}>
          <CellInner visible={visible} align="right" py={rowPy}>
            {node.closing_balance > 0 ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <MonoAmount
                  value={node.closing_balance}
                  color={isDebit ? "#DC2626" : "#000000"}
                  size={isCategory ? "0.82rem" : "0.78rem"}
                  weight={isCategory || isGroup ? 700 : 500}
                />
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: isDebit ? "#DC2626" : "#000000",
                    fontFamily: "'IBM Plex Mono', monospace",
                  }}
                >
                  {isDebit ? "Db" : "Cr"}
                </Typography>
              </Box>
            ) : (
              <Dash />
            )}
          </CellInner>
        </TableCell>
      </TableRow>
    </Tooltip>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────
const TrialBalanceAllGroups = ({
  reportData,
  asOnDate,
  fromDate,
  toDate,
  filterMode,
  onShowZeroChange,
}) => {
  const { userInfo, defaultFYId, defaultFY } =
    useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id || 0;
  // Keeps the voucher drill-down inside the host module (pawn keeps its chrome).
  const { go: goAccounts } = useAccountsNav();

  const [expandedIds, setExpandedIds] = useState(
    () =>
      new Set(reportData?.categories?.map((c) => String(c.account_id)) || []),
  );
  const [showZeroBalances, setShowZeroBalances] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [ledgerError, setLedgerError] = useState("");
  const [dialogMonthFilter, setDialogMonthFilter] = useState("");

  const filteredTransactions = useMemo(() => {
    if (!ledgerData?.transactions) return [];
    const sorted = [...ledgerData.transactions].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
    if (!dialogMonthFilter) return sorted;
    return sorted.filter(
      (txn) => format(new Date(txn.date), "yyyy-MM") === dialogMonthFilter,
    );
  }, [ledgerData, dialogMonthFilter]);

  if (!reportData) return null;

  const {
    categories,
    total_debit,
    total_credit,
    is_balanced,
    difference,
    opening_debit,
    opening_credit,
    fy,
  } = reportData;
  const allExpandableIds = collectAllIds(categories);
  const flatRows = flattenTree(categories, expandedIds, showZeroBalances);

  const handleToggle = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleExpandAll = () => setExpandedIds(new Set(allExpandableIds));
  const handleCollapseAll = () =>
    setExpandedIds(new Set(categories.map((c) => String(c.account_id))));

  const handleShowZeroChange = (event) => {
    setShowZeroBalances(event.target.checked);
    if (onShowZeroChange) onShowZeroChange(event.target.checked);
  };

  const handleAccountDoubleClick = async (node) => {
    setSelectedAccount(node);
    setDialogOpen(true);
    setLedgerError("");
    setLedgerData(null);
    setDialogMonthFilter("");
    try {
      setLoadingLedger(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/accounts/individual-ledger`,
        {
          store_id: parseInt(store_id),
          filter_type: "Account",
          account_id: node.account_id,
          fy_id: defaultFYId,
          from_date: filterMode === "between" ? fromDate : `${defaultFY}-04-01`,
          to_date: filterMode === "between" ? toDate : asOnDate,
        },
      );
      if (response.data.success) {
        setLedgerData(response.data.data);
      } else {
        setLedgerError(response.data.message || "Failed to fetch ledger");
      }
    } catch (err) {
      setLedgerError(err.response?.data?.message || "Error fetching ledger");
    } finally {
      setLoadingLedger(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAccount(null);
    setLedgerData(null);
    setLedgerError("");
    setDialogMonthFilter("");
  };

  const handleTxnRowDoubleClick = (txn) => {
    goAccounts("/accounts-entry", {
      state: { voucher_no: txn.voucher_number },
    });
  };

  const hCell = (align = "left", color = "#64748B") => ({
    py: "11px",
    px: "16px",
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 700,
    fontSize: "0.69rem",
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color,
    textAlign: align,
    backgroundColor: "#F8FAFC",
    borderBottom: "2px solid #E2E8F0",
    position: "sticky",
    top: 0,
    zIndex: 3,
    boxShadow: "0 2px 6px -2px rgba(0,0,0,0.07)",
  });

  return (
    <Box sx={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: "10px",
        }}
      >
        {/* Date/FY Display */}
        {fy ? (
          <Typography
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.7rem",
              color: "#94A3B8",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            FY {fy}-{String(fy + 1).slice(-2)}
          </Typography>
        ) : filterMode === "as_on" && asOnDate ? (
          <Typography
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.7rem",
              color: "#94A3B8",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            As on {format(new Date(asOnDate), "dd/MM/yyyy")}
          </Typography>
        ) : filterMode === "between" && fromDate && toDate ? (
          <Typography
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.7rem",
              color: "#94A3B8",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            Period: {format(new Date(fromDate), "dd/MM/yyyy")} to{" "}
            {format(new Date(toDate), "dd/MM/yyyy")}
          </Typography>
        ) : (
          <Box />
        )}

        <Box
          sx={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
            ml: "auto",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={showZeroBalances}
                onChange={handleShowZeroChange}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2563EB" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2563EB",
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.75rem",
                  color: "#475569",
                  fontWeight: 500,
                }}
              >
                Show Zero Balances
              </Typography>
            }
            sx={{ m: 0 }}
          />
          <Box sx={{ display: "flex", gap: "4px" }}>
            {[
              {
                title: "Expand all",
                icon: <UnfoldMore sx={{ fontSize: 16 }} />,
                fn: handleExpandAll,
              },
              {
                title: "Collapse all",
                icon: <UnfoldLess sx={{ fontSize: 16 }} />,
                fn: handleCollapseAll,
              },
            ].map(({ title, icon, fn }) => (
              <Tooltip key={title} title={title} placement="top">
                <IconButton
                  size="small"
                  onClick={fn}
                  sx={{
                    border: "1px solid #E2E8F0",
                    borderRadius: "6px",
                    p: "4px",
                    color: "#64748B",
                    transition: "all 150ms ease",
                    "&:hover": {
                      backgroundColor: "#F1F5F9",
                      borderColor: "#94A3B8",
                      color: "#334155",
                    },
                  }}
                >
                  {icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Main Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: "10px",
          maxHeight: "75vh",
          overflow: "auto",
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: "fixed",
            minWidth: filterMode === "between" ? 800 : 640,
          }}
        >
          <colgroup>
            <col style={{ width: 36 }} />
            <col />
            {filterMode === "between" && <col style={{ width: 140 }} />}
            <col style={{ width: 140 }} />
            <col style={{ width: 140 }} />
            <col style={{ width: 140 }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...hCell(), width: 36, px: "6px" }} />
              <TableCell sx={hCell("left", "#475569")}>
                Account / Group
              </TableCell>
              {filterMode === "between" && (
                <TableCell sx={hCell("right", "#475569")}>Opening</TableCell>
              )}
              <TableCell sx={hCell("right", "#DC2626")}>
                {filterMode === "between" ? "Period " : ""}Debit
              </TableCell>
              <TableCell sx={hCell("right", "#000000")}>
                {filterMode === "between" ? "Period " : ""}Credit
              </TableCell>
              <TableCell sx={hCell("right", "#475569")}>
                {filterMode === "between" ? "Closing" : "Balance"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flatRows.map(({ node, depth, visible }) => (
              <TrialRow
                key={`${node.account_type}-${node.account_id}`}
                node={node}
                depth={depth}
                visible={visible}
                expandedIds={expandedIds}
                onToggle={handleToggle}
                onAccountDoubleClick={handleAccountDoubleClick}
                filterMode={filterMode}
              />
            ))}

            {/* Grand Total */}
            <TableRow
              sx={{
                backgroundColor: "#F1F5F9",
                "& td": {
                  borderTop: "2px solid #CBD5E1",
                  borderBottom: 0,
                  py: "10px",
                  px: "16px",
                },
              }}
            >
              <TableCell sx={{ px: "6px !important" }} />
              <TableCell>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: "#1E293B",
                      letterSpacing: "0.02em",
                    }}
                  >
                    Grand Total
                  </Typography>
                  {is_balanced ? (
                    <Box
                      sx={{
                        px: "8px",
                        py: "2px",
                        borderRadius: "4px",
                        backgroundColor: "#DCFCE7",
                        border: "1px solid #86EFAC",
                        color: "#15803D",
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        fontFamily: "'DM Sans', sans-serif",
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                      }}
                    >
                      ✓ Balanced
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        px: "8px",
                        py: "2px",
                        borderRadius: "4px",
                        backgroundColor: "#FEF3C7",
                        border: "1px solid #FCD34D",
                        color: "#92400E",
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        fontFamily: "'IBM Plex Mono', monospace",
                      }}
                    >
                      Diff {formatCurrency(difference)}
                    </Box>
                  )}
                </Box>
              </TableCell>
              {filterMode === "between" && (
                <TableCell align="right">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: "4px",
                    }}
                  >
                    {opening_debit > opening_credit ? (
                      <>
                        <MonoAmount
                          value={opening_debit - opening_credit}
                          color="#DC2626"
                          size="0.82rem"
                          weight={700}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: "#DC2626",
                            fontFamily: "'IBM Plex Mono', monospace",
                          }}
                        >
                          Db
                        </Typography>
                      </>
                    ) : opening_credit > opening_debit ? (
                      <>
                        <MonoAmount
                          value={opening_credit - opening_debit}
                          color="#000000"
                          size="0.82rem"
                          weight={700}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: "#000000",
                            fontFamily: "'IBM Plex Mono', monospace",
                          }}
                        >
                          Cr
                        </Typography>
                      </>
                    ) : (
                      <Dash />
                    )}
                  </Box>
                </TableCell>
              )}
              <TableCell align="right">
                <Dash />
              </TableCell>
              <TableCell align="right">
                <Dash />
              </TableCell>
              <TableCell align="right">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: "4px",
                  }}
                >
                  {total_debit > total_credit ? (
                    <>
                      <MonoAmount
                        value={total_debit - total_credit}
                        color="#DC2626"
                        size="0.82rem"
                        weight={700}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          color: "#DC2626",
                          fontFamily: "'IBM Plex Mono', monospace",
                        }}
                      >
                        Db
                      </Typography>
                    </>
                  ) : total_credit > total_debit ? (
                    <>
                      <MonoAmount
                        value={total_credit - total_debit}
                        color="#000000"
                        size="0.82rem"
                        weight={700}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          color: "#000000",
                          fontFamily: "'IBM Plex Mono', monospace",
                        }}
                      >
                        Cr
                      </Typography>
                    </>
                  ) : (
                    <Dash />
                  )}
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* LEDGER DIALOG */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: "80vh" } }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Account Ledger — {selectedAccount?.account_name}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <Close />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Account ID: {selectedAccount?.account_id} | Category:{" "}
            {selectedAccount?.account_category || "N/A"}
          </Typography>
          <Typography variant="caption" color="primary">
            All transactions — use month filter to narrow down
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {loadingLedger && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {ledgerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {ledgerError}
            </Alert>
          )}

          {ledgerData && !loadingLedger && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Opening Balance
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {formatCurrency(ledgerData.opening_balance)}
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          display: "inline-block",
                          px: 1,
                          py: 0.2,
                          borderRadius: 1,
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: "white",
                          backgroundColor: "#2563EB",
                        }}
                      >
                        {ledgerData.opening_balance_type}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Total Debit
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "#DC2626" }}
                      >
                        {formatCurrency(ledgerData.total_debit)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {ledgerData.transaction_count} transactions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Total Credit
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        {formatCurrency(ledgerData.total_credit)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {ledgerData.transaction_count} transactions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Month Filter */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <TextField
                  type="month"
                  size="small"
                  label="Filter by Month"
                  value={dialogMonthFilter}
                  onChange={(e) => setDialogMonthFilter(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: 200,
                    "& input::-webkit-datetime-edit": {
                      color: dialogMonthFilter ? "inherit" : "transparent",
                    },
                    "& input:focus::-webkit-datetime-edit": {
                      color: "inherit",
                    },
                  }}
                />
                {dialogMonthFilter && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setDialogMonthFilter("")}
                  >
                    Clear Filter
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary">
                  Showing {filteredTransactions.length} of{" "}
                  {ledgerData.transactions.length} transactions
                </Typography>
              </Box>

              {/* Transactions Table */}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Voucher No.
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Narration</TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: "#DC2626" }}
                      >
                        Debit
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        Credit
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Balance
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Opening Balance Row */}
                    <TableRow sx={{ backgroundColor: "grey.50" }}>
                      <TableCell colSpan={3}>
                        <strong>Opening Balance</strong>
                      </TableCell>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="right">
                        {formatCurrency(ledgerData.opening_balance)}{" "}
                        <BalanceChip type={ledgerData.opening_balance_type} />
                      </TableCell>
                    </TableRow>

                    {/* Transactions */}
                    {filteredTransactions.length > 0 ? (
                      filteredTransactions.map((txn, index) => (
                        <Tooltip
                          key={index}
                          title="Double-click to open voucher"
                          placement="left"
                        >
                          <TableRow
                            hover
                            onDoubleClick={() => handleTxnRowDoubleClick(txn)}
                            sx={{
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "action.hover" },
                            }}
                          >
                            <TableCell>
                              {format(new Date(txn.date), "dd/MM/yyyy")}
                            </TableCell>
                            <TableCell>{txn.voucher_number}</TableCell>
                            <TableCell>{txn.narration}</TableCell>
                            <TableCell align="right" sx={{ color: "#DC2626" }}>
                              {txn.debit_amount > 0
                                ? formatCurrency(txn.debit_amount)
                                : "-"}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: "text.primary" }}
                            >
                              {txn.credit_amount > 0
                                ? formatCurrency(txn.credit_amount)
                                : "-"}
                            </TableCell>
                            <TableCell align="right">
                              <strong>
                                {formatCurrency(txn.running_balance)}
                              </strong>{" "}
                              <BalanceChip type={txn.balance_type} />
                            </TableCell>
                          </TableRow>
                        </Tooltip>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">
                            {dialogMonthFilter
                              ? "No transactions for selected month"
                              : "No transactions found"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Closing Balance Row */}
                    <TableRow sx={{ backgroundColor: "grey.50" }}>
                      <TableCell colSpan={3}>
                        <strong>Closing Balance</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {formatCurrency(ledgerData.total_debit)}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {formatCurrency(ledgerData.total_credit)}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {formatCurrency(ledgerData.closing_balance)}
                        </strong>{" "}
                        <BalanceChip type={ledgerData.closing_balance_type} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrialBalanceAllGroups;
